
import Vue from "vue";
import { imageStaticUrl, thumbStaticUrl } from "@/utils/assetImage";
import { enqueueImageLoad } from "@/utils/imageLoadQueue";

export default Vue.extend({
  name: "ProgressiveImage",
  props: {
    /** 相对 assets 路径，走 Nginx 静态 URL */
    imagePath: { type: String, default: "" },
    /** 预取的缩略图 URL */
    prefetchedThumb: { type: String, default: "" },
    /** 静态资源直链（如 dist/public 下图） */
    fullSrc: { type: String, default: "" },
    thumbSrc: { type: String, default: "" },
    alt: { type: String, default: "" },
    imgClass: { type: [String, Array, Object], default: "" },
    placeholderClass: { type: [String, Array, Object], default: "" },
    eager: { type: Boolean, default: false },
    upgradeToHd: { type: Boolean, default: true },
    thumbMaxEdge: { type: Number, default: undefined },
  },
  data: () => ({
    displaySrc: "" as string,
    hdApplied: false,
    loadGeneration: 0,
    _destroyed: false,
    _observer: null as IntersectionObserver | null,
    _pendingIdle: null as number | null,
    _pendingTimer: null as ReturnType<typeof setTimeout> | null,
    _visibilityObserved: false,
  }),
  computed: {
    imgClasses(): (string | Record<string, boolean>)[] {
      const base = Array.isArray(this.imgClass)
        ? this.imgClass
        : this.imgClass
          ? [this.imgClass]
          : [];
      return [
        ...base,
        {
          "progressive-image--loading": !this.hdApplied && !!this.displaySrc,
          "progressive-image--hd": this.hdApplied,
        },
      ];
    },
    resolvedFullSrc(): string {
      if (this.fullSrc) return this.fullSrc;
      if (this.imagePath) return imageStaticUrl(this.imagePath);
      return "";
    },
    resolvedThumbSrc(): string {
      if (this.thumbSrc) return this.thumbSrc;
      if (this.prefetchedThumb) return this.prefetchedThumb;
      if (this.imagePath) return thumbStaticUrl(this.imagePath);
      return "";
    },
  },
  watch: {
    imagePath: { handler: "scheduleReload", immediate: true },
    prefetchedThumb: { handler: "scheduleReload" },
    fullSrc: { handler: "scheduleReload" },
    thumbSrc: { handler: "scheduleReload" },
  },
  mounted() {
    if (this.eager) {
      this.reload();
    } else {
      this.observeVisibility();
    }
  },
  beforeDestroy() {
    this._destroyed = true;
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this._pendingIdle !== null) {
      try { cancelIdleCallback(this._pendingIdle); } catch { /* ignore */ }
      this._pendingIdle = null;
    }
    if (this._pendingTimer !== null) {
      clearTimeout(this._pendingTimer);
      this._pendingTimer = null;
    }
  },
  methods: {
    observeVisibility() {
      const el = this.$refs.placeholder as HTMLElement | undefined;
      if (!el || this._visibilityObserved) return;
      this._visibilityObserved = true;

      this._observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            if (this._observer) {
              this._observer.disconnect();
              this._observer = null;
            }
            this.reload();
          }
        },
        { rootMargin: "200px 0px", threshold: 0 }
      );
      this._observer.observe(el);
    },
    scheduleReload() {
      if (this._destroyed) return;
      if (this.eager) {
        this.reload();
      } else if (!this._visibilityObserved) {
        return;
      } else {
        this.reload();
      }
    },
    reload() {
      if (this._destroyed) return;
      this.hdApplied = false;
      this.displaySrc = "";
      this.loadGeneration += 1;
      const gen = this.loadGeneration;

      const full = this.resolvedFullSrc;
      if (!full) return;

      const thumb = this.resolvedThumbSrc || full;
      this.loadViaUrl(gen, full, thumb);
    },
    loadViaUrl(gen: number, full: string, thumb: string) {
      const initial = thumb || full;
      this.displaySrc = initial;

      if (this.upgradeToHd && thumb && thumb !== full) {
        this.scheduleUrlHd(gen, full);
      } else {
        this.hdApplied = true;
      }
    },
    scheduleUrlHd(gen: number, full: string) {
      const run = () => {
        if (this._destroyed) return;
        enqueueImageLoad(
          () =>
            new Promise<void>((resolve, reject) => {
              if (this._destroyed) { reject(); return; }
              const img = new Image();
              img.onload = () => resolve();
              img.onerror = () => reject();
              img.src = full;
            })
        )
          .then(() => {
            if (this._destroyed) return;
            if (gen === this.loadGeneration && this.resolvedFullSrc === full) {
              this.displaySrc = full;
              this.hdApplied = true;
            }
          })
          .catch(() => {
            /* 保留缩略图 */
          });
      };
      if (typeof requestIdleCallback === "function") {
        this._pendingIdle = requestIdleCallback(() => {
          this._pendingIdle = null;
          run();
        }, { timeout: 5000 });
      } else {
        this._pendingTimer = setTimeout(() => {
          this._pendingTimer = null;
          run();
        }, 400);
      }
    },
    onLoad() {
      this.$emit("load");
    },
    onError() {
      if (this._destroyed) return;
      const full = this.resolvedFullSrc;
      const thumb = this.resolvedThumbSrc;
      if (thumb && this.displaySrc === thumb && full && full !== thumb) {
        this.displaySrc = full;
        this.hdApplied = true;
      }
      this.$emit("error");
    },
  },
});
